// POS Header Component
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { LogOut, PanelRightClose, PanelRight, Wallet, KeyRound } from 'lucide-react';
import { BUSINESS_LOGOS } from './constants';

export function POSHeader({
  branches,
  selectedBranch,
  setSelectedBranch,
  user,
  logout,
  ticketPanelOpen,
  setTicketPanelOpen,
  setShowCashRegister,
  cashRegisterOpen,
  setShowAttendanceCode,
}) {
  const getCurrentBranch = () => branches.find(b => b.id === selectedBranch);
  
  const getBusinessLogo = () => {
    const branch = getCurrentBranch();
    if (branch?.business_name) {
      return BUSINESS_LOGOS[branch.business_name] || BUSINESS_LOGOS['The Holland Barbershop'];
    }
    return BUSINESS_LOGOS['The Holland Barbershop'];
  };

  const getBusinessName = () => {
    const branch = getCurrentBranch();
    return branch?.business_name || 'The Holland Barbershop';
  };

  return (
    <header className="h-16 border-b border-zinc-800 flex items-center justify-between px-6">
      <div className="flex items-center gap-3">
        <img 
          src={getBusinessLogo()} 
          alt={getBusinessName()}
          className="w-10 h-10 object-contain rounded-sm"
          data-testid="pos-business-logo"
        />
        <div>
          <h1 className="font-heading text-lg text-white">{getBusinessName()}</h1>
          <p className="text-xs text-zinc-500">
            {getCurrentBranch()?.name || user?.branch_name || 'Sin sucursal'}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {/* Branch selector - disabled for manager (encargado) users */}
        {user?.role === 'master_admin' || branches.length > 1 ? (
          <Select 
            value={selectedBranch} 
            onValueChange={setSelectedBranch}
            disabled={user?.role === 'manager'}
          >
            <SelectTrigger 
              className={`w-[200px] bg-zinc-800 border-zinc-700 text-white text-sm h-9 ${
                user?.role === 'manager' ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              <SelectValue placeholder="Seleccionar sucursal" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700 max-h-[300px]">
              {branches.map((branch) => (
                <SelectItem 
                  key={branch.id} 
                  value={branch.id} 
                  className="text-white text-sm"
                >
                  <span className="flex items-center gap-2">
                    <span className="text-zinc-500 text-xs">{branch.business_name?.split(' ')[0]}</span>
                    {branch.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <div className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-sm text-white text-sm">
            {getCurrentBranch()?.name || 'Sucursal'}
          </div>
        )}
        
        {/* Cash Register Button */}
        <button
          onClick={() => setShowCashRegister?.(true)}
          className={`flex items-center gap-2 px-3 py-2 border rounded-sm transition-colors ${
            cashRegisterOpen 
              ? 'bg-emerald-500/10 hover:bg-emerald-500/20 border-emerald-500/30 text-emerald-500' 
              : 'bg-red-500/10 hover:bg-red-500/20 border-red-500/30 text-red-500'
          }`}
          data-testid="pos-cash-register-btn"
          title="Corte de Caja"
        >
          <Wallet className="w-4 h-4" />
          <span className="text-sm font-medium">
            {cashRegisterOpen ? 'Caja Abierta' : 'Abrir Caja'}
          </span>
        </button>

        {/* Attendance Code Button (visible only when cash register is open) */}
        {cashRegisterOpen && setShowAttendanceCode && (
          <button
            onClick={() => setShowAttendanceCode(true)}
            className="flex items-center gap-2 px-3 py-2 border rounded-sm transition-colors bg-gold/10 hover:bg-gold/20 border-gold/30 text-gold"
            data-testid="pos-attendance-code-btn"
            title="Generar código de asistencia para barberos"
          >
            <KeyRound className="w-4 h-4" />
            <span className="text-sm font-medium">Código Asistencia</span>
          </button>
        )}
        
        <span className="text-sm text-zinc-400">{user?.name}</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={logout}
          className="text-zinc-400 hover:text-white"
          data-testid="pos-logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Salir
        </Button>
        <button
          onClick={() => setTicketPanelOpen(!ticketPanelOpen)}
          className="w-8 h-8 rounded-sm bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
          data-testid="toggle-ticket-panel-btn"
          title={ticketPanelOpen ? 'Ocultar ticket' : 'Mostrar ticket'}
        >
          {ticketPanelOpen ? <PanelRightClose className="w-4 h-4" /> : <PanelRight className="w-4 h-4" />}
        </button>
      </div>
    </header>
  );
}
